import { FC, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { AppLoader } from 'components/Core/AppLoader';
import { Button } from 'components/Core/Button';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { TranslatesObject } from 'types/common';
import { TranslationsForMobileFormTranslations } from './translations';
import { ITranslationsForMobileFormProps, TranslationsForMobileFormTransFields } from './types';
import styles from './translationForMobileForm.module.scss';

export const TranslationsForMobileForm: FC<ITranslationsForMobileFormProps> = ({
  translations,
  onSave,
  title,
  isLoading = false,
  saveHint,
  confirm,
}) => {
  const {
    mainStore: { mobileLanguages, getMobileLanguagesList },
    popUpStore: { openPopUp, closePopUp },
  } = useStore();

  const { t } = useMyTranslations<TranslationsForMobileFormTransFields>([TranslationsForMobileFormTranslations.name]);

  const { isFetching } = useQuery('getMobileLanguagesList', getMobileLanguagesList, {
    enabled: !mobileLanguages?.length,
    retry: false,
  });

  const useFormMethods = useForm<TranslatesObject>({
    defaultValues: translations,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useFormMethods;

  const isBusy = isSubmitting || isLoading;

  // Validation first; with `confirm` the save waits for the popup's OK.
  const onSubmitHandler = useCallback(
    (inputData: TranslatesObject) => {
      if (!confirm) {
        onSave(inputData);
        return;
      }
      openPopUp({
        formName: 'confirmation',
        ...confirm,
        cb: () => {
          closePopUp();
          onSave(inputData);
        },
      });
    },
    [onSave, confirm, openPopUp, closePopUp]
  );

  if (isFetching) return <AppLoader />;

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={`${styles.leftSection}`}>
            <div className={`${styles.formSection}`}>
              <h6 className={`${styles.title} ${styles.titleWithMargin}`}>{title}</h6>
              {mobileLanguages?.map((item) => (
                <ControlledInput key={item} name={item} type='text' label={item} required disabled={isBusy} />
              ))}
            </div>
            <div className={styles.bottomSection}>
              <Button
                title={t('save')}
                onClick={() => !isBusy && handleSubmit(onSubmitHandler)()}
                isDisabled={isBusy}
              />
              {saveHint && <p className={styles.saveHint}>{saveHint}</p>}
            </div>
          </section>
        </form>
      </div>
    </FormProvider>
  );
};

export default observer(TranslationsForMobileForm);
